import { useConfig } from "$global-providers/config-provider/provider";
import { isDevEnvironment } from "$routes/_lib/environment";
import {
  type ApolloClient,
  ApolloError,
  type DocumentNode,
  type OperationVariables,
  useApolloClient,
} from "@apollo/client";
import * as Sentry from "@sentry/react";
import { type Duration, subMinutes } from "date-fns";
import { toMilliseconds } from "duration-fns";
import type { SyncableCheckpoint } from "elephant-web/gql";
import type { OperationDefinitionNode } from "graphql";
import { useEffect, useRef, useState } from "react";
import type { RxCollection, RxDatabase, RxError, WithDeleted } from "rxdb";
import {
  type RxReplicationState,
  replicateRxCollection,
} from "rxdb/plugins/replication";
import { useInterval, useLocalStorage } from "usehooks-ts";
import { RXDB_SYNC_CHECKPOINT_COLLECTION_NAME } from "../collections/sync-checkpoint";
import { clearExpiredData } from "../hooks/use-data-expiry-interval";
import type { RxDBCollections } from "../provider";
import {
  createReplicationBackoffGate,
  type ReplicationBackoffGate,
} from "./create-replication-backoff-gate";

const RETRY_TIME_IN_MS = Math.floor(Math.random() * 5000) + 3000;

export type ReplicationPullConfig<GQLPullOutput> = {
  query: DocumentNode;
  buildInputVariables: (
    checkpoint: SyncableCheckpoint | null,
    batchSize: number,
  ) => Promise<OperationVariables>;
  buildPullCheckpoint: (
    gqlDocument: GQLPullOutput,
  ) => Promise<SyncableCheckpoint>;
  reSyncInterval?: Duration;
  onReplicationComplete?: () => void;
};

export interface ReplicationOptions<RX, GQLPullOutput, GQLPushInput> {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RX>;
  push?: {
    mutation: DocumentNode;
    mapRxToGql: (rxEntity: WithDeleted<RX>) => GQLPushInput;
    /**
     * Optional predicate run before `mapRxToGql` over every pending push doc.
     * Docs the filter rejects are silently dropped from this push call —
     * RxDB treats them as successfully handled (they're not returned as
     * conflicts) so the checkpoint advances. Used by the per-user
     * notification replications on shared devices to keep one user's
     * replication identity from pushing another user's rows to the server
     * (the row stays on disk for its real owner, who'll push it via their
     * own replication identity).
     */
    filter?: (doc: WithDeleted<RX>) => boolean;
  };
  pull?: ReplicationPullConfig<GQLPullOutput>;
  mapGqlToRx: (gqlEntity: GQLPullOutput) => WithDeleted<RX>;
  batchSize?: number;
  expireData?: boolean;
  metadataLocalStorageKeySuffix: string;
  /** When provided, appended to the RxDB replicationIdentifier to keep concurrent
   *  instances of the same hook (e.g. global vs per-encounter) from sharing internal state. */
  replicationIdentifierSuffix?: string;
  enabled?: boolean;
}

export type CollectionReplicationState = {
  isPulling: boolean;
  failedToPull?: boolean;
  lastPulledAt?: Date;
  earliestNonPushedChangeAt?: Date; // null when the collection has been pushed
};

export const getObjectKeys = (
  object: { [key: string]: unknown },
  existingKey?: string,
) => {
  return Object.entries(object).reduce((acc, [key, value]) => {
    const newKey = existingKey ? `${existingKey}.${key}` : key;
    acc.push(newKey);
    if (value !== null && typeof value === "object")
      acc.push(...getObjectKeys(value as { [key: string]: unknown }, newKey));
    return acc;
  }, []);
};

export const useGqlReplication = <RX, GQLPullOutput, GQLPushInput>({
  db,
  collection,
  push,
  pull,
  mapGqlToRx,
  batchSize = 100,
  expireData = false,
  metadataLocalStorageKeySuffix,
  replicationIdentifierSuffix,
  enabled = true,
}: ReplicationOptions<
  RX,
  GQLPullOutput,
  GQLPushInput
>): CollectionReplicationState => {
  const { rxdb, federationPath } = useConfig();
  const apolloClient = useApolloClient();
  const replicationState =
    useRef<RxReplicationState<RX, SyncableCheckpoint>>(null);
  const backoffGate = useRef<ReplicationBackoffGate>(null);
  if (backoffGate.current === null) {
    backoffGate.current = createReplicationBackoffGate();
  }
  const [isPulling, setIsPulling] = useState(false);
  const [lastPulledAt, setLastPulledAt] = useLocalStorage<string | null>(
    `lastPulledAt-${metadataLocalStorageKeySuffix}`,
    null,
  );
  const [failedToPull, setFailedToPull] = useLocalStorage<boolean>(
    `failedToPull-${metadataLocalStorageKeySuffix}`,
    false,
  );
  const [earliestNonPushedChangeAt, setEarliestNonPushedChangeAt] =
    useLocalStorage<string | null>(
      `earliestNonPushedChangeAt-${metadataLocalStorageKeySuffix}`,
      null,
    );

  // failedToPull is persisted, so a flag left true by an earlier failure would
  // survive forever once the pull is no longer configured (nothing runs to
  // clear it) and keep the "Update failed" banner up.
  useEffect(() => {
    if (!pull) setFailedToPull(false);
  }, [pull, setFailedToPull]);

  useInterval(
    () => {
      if (!db || !collection) return;
      if (!expireData) {
        console.debug("Data expiry not enabled, skipping data expiry.", {
          collectionName: collection.name,
        });
        return;
      }
      if (!replicationState.current) {
        console.debug(
          "Collection or replication state not provided, skipping data expiry.",
          { collectionName: collection.name },
        );
        return;
      }
      clearExpiredData({
        replicationState: replicationState.current,
        maxAge: { minutes: rxdb?.dataExpiryMaxAgeMins ?? 60 * 24 * 2 },
        clearPersistedCheckpoints: async () => {
          await db[RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]
            .find({
              selector: {
                collectionName: collection.name,
                "checkpoint.updatedAt": {
                  $lte: subMinutes(
                    new Date(),
                    rxdb?.dataExpiryMaxAgeMins ?? 60 * 24 * 2,
                  ).toISOString(),
                },
              },
            })
            .remove();
        },
      });
    },
    2 * 60 * 1000,
  );

  useInterval(
    () => {
      if (
        !pull?.reSyncInterval ||
        !db ||
        !collection ||
        !replicationState.current ||
        isPulling
      ) {
        return;
      }

      replicationState.current.reSync();
    },
    pull?.reSyncInterval ? toMilliseconds(pull.reSyncInterval) : null,
  );

  useEffect(() => {
    const onReconnect = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        return;
      }
      backoffGate.current?.reset();
      replicationState.current?.reSync();
    };
    window.addEventListener("online", onReconnect);
    document.addEventListener("visibilitychange", onReconnect);
    return () => {
      window.removeEventListener("online", onReconnect);
      document.removeEventListener("visibilitychange", onReconnect);
    };
  }, []);

  useEffect(() => {
    if (!db || !collection) return;
    if (!pull && !push) return; // Nothing to replicate — skip to avoid RxDB UT3 error
    let errorSubscription: { unsubscribe: () => void };
    let replicationActiveSubscription: { unsubscribe: () => void };
    // We need to use this internal flag to ensure that the pull.onReplicationComplete
    // callback is only called once the pull from network AND the write to the DB has completed.
    // The isPulling state can't be relied on because it is still false while isActive is false
    // initially, leading to the callback being called prematurely.
    let _isPullFromNetworkComplete = false;
    (async () => {
      replicationState.current = replicateRxCollection<RX, SyncableCheckpoint>({
        collection,
        replicationIdentifier: `gql-${collection.name}-replication-to-${federationPath}${replicationIdentifierSuffix ? `-${replicationIdentifierSuffix}` : ""}`,
        pull: pull
          ? {
              handler: await buildPullHandler({
                query: pull.query,
                apolloClient,
                mapGqlToRx,
                buildInputVariables: pull.buildInputVariables,
                buildCheckpoint: pull.buildPullCheckpoint,
                setIsPulling,
                setLastPulledAt,
                setFailedToPull,
                backoffGate: backoffGate.current,
                onPullFromNetworkComplete: () => {
                  _isPullFromNetworkComplete = true;
                },
              }),
              batchSize,
            }
          : undefined,
        push: push
          ? {
              handler: async (args) => {
                const handler = buildPushHandler({
                  mutation: push.mutation,
                  apolloClient,
                  mapRxToGql: push.mapRxToGql,
                  mapGqlToRx,
                });

                // Apply optional pre-push filter (e.g. user-scoped notification
                // replications on shared devices). Filtered-out docs aren't
                // returned to RxDB as conflicts, so the checkpoint advances
                // and they're considered handled for this replication
                // identity. The row stays on disk for its owner.
                const filtered = push.filter
                  ? args.filter((doc) =>
                      push.filter ? push.filter(doc.newDocumentState) : true,
                    )
                  : args;
                if (filtered.length === 0) return [];

                // Mark unsynced BEFORE the backoff wait so the UI reflects pending
                // changes immediately during an outage, not only after the backoff
                // window elapses. Only set when previously synced (curr null) so we
                // don't push the timestamp forward on an already-unsynced collection.
                setEarliestNonPushedChangeAt(
                  (curr) => curr ?? new Date().toISOString(),
                );
                await backoffGate.current.waitIfBackingOff();
                try {
                  const result = await handler(filtered);
                  backoffGate.current.onSuccess();
                  setEarliestNonPushedChangeAt(null);
                  return result;
                } catch (e) {
                  backoffGate.current.onError();
                  throw e;
                }
              },
              batchSize,
            }
          : undefined,
        retryTime: RETRY_TIME_IN_MS,
        live: true,
        autoStart: enabled,
      });

      if (pull?.onReplicationComplete) {
        replicationActiveSubscription =
          replicationState.current.active$.subscribe((isActive) => {
            // We need to combine the active$ subscription with the _isPullFromNetworkComplete state
            // because the active$ subscription emits after the write to the DB has completed.
            if (!isActive && _isPullFromNetworkComplete) {
              pull.onReplicationComplete();
              _isPullFromNetworkComplete = false;
            }
          });
      }

      errorSubscription = replicationState.current.error$.subscribe((err) => {
        console.error("Replication error", {
          collectionName: collection.name,
          name: (err as Error).name,
          ...(isDevEnvironment() ? { error: err } : {}),
        });

        if (!isUnderlyingNetworkError(err)) {
          Sentry.captureException(err, (scope) =>
            scope.setTag("rxCollectionName", collection.name),
          );
        }
      });
      await replicationState.current?.awaitInSync();
    })().catch((err) => {
      console.error("Unhandled error in replication effect", {
        collectionName: collection.name,
        name: (err as Error).name,
        ...(isDevEnvironment() ? { error: err } : {}),
      });
      setIsPulling(false);
    });

    return () => {
      replicationState.current?.cancel().catch((err) => {
        console.error("Error cancelling replication", {
          collectionName: collection.name,
          name: (err as Error).name,
          ...(isDevEnvironment() ? { error: err } : {}),
        });
      });
      errorSubscription?.unsubscribe();
      replicationState.current = null;
      setIsPulling(false);
      replicationActiveSubscription?.unsubscribe();
    };
  }, [
    db,
    collection,
    apolloClient,
    federationPath,
    push,
    pull,
    mapGqlToRx,
    batchSize,
    setLastPulledAt,
    setEarliestNonPushedChangeAt,
    setFailedToPull,
    enabled,
    replicationIdentifierSuffix,
  ]);

  return {
    isPulling,
    failedToPull,
    lastPulledAt: lastPulledAt ? new Date(lastPulledAt) : null,
    earliestNonPushedChangeAt: earliestNonPushedChangeAt
      ? new Date(earliestNonPushedChangeAt)
      : null,
  };
};

const getFieldResolverName = (query: DocumentNode): string => {
  const queryDef = query.definitions.find(
    (def): def is OperationDefinitionNode =>
      def.kind === "OperationDefinition" &&
      (def.operation === "query" || def.operation === "mutation"),
  );
  const selectionSet = queryDef.selectionSet.selections[0];
  if (selectionSet.kind === "Field") {
    return selectionSet.name.value;
  }
  throw new Error("Unable to find field resolver name in query or mutation");
};

async function buildPullHandler<RX, GQLOutput, PullVariables>({
  query,
  apolloClient,
  mapGqlToRx,
  buildInputVariables,
  buildCheckpoint,
  setIsPulling,
  setLastPulledAt,
  setFailedToPull,
  backoffGate,
  onPullFromNetworkComplete,
}: {
  query: DocumentNode;
  apolloClient: ApolloClient<object>;
  mapGqlToRx: (gqlDoc: GQLOutput) => RX;
  buildInputVariables: (
    lastPulledCheckpoint: SyncableCheckpoint | null,
    currentBatchSize: number,
  ) => Promise<PullVariables>;
  buildCheckpoint: (gqlDocument: GQLOutput) => Promise<SyncableCheckpoint>;
  setIsPulling: (isPulling: boolean) => void;
  setLastPulledAt: (lastPulledAt: string) => void;
  setFailedToPull: (failedToPull: boolean) => void;
  backoffGate: ReplicationBackoffGate;
  onPullFromNetworkComplete?: () => void;
}) {
  const fieldName = getFieldResolverName(query);
  return async (
    checkpoint: SyncableCheckpoint | null,
    currentBatchSize: number,
  ) => {
    await backoffGate.waitIfBackingOff();
    setIsPulling(true);
    try {
      const result = await apolloClient.query<{
        [key: string]: { documents: GQLOutput[] };
      }>({
        query: query,
        fetchPolicy: "network-only",
        variables: await buildInputVariables(checkpoint, currentBatchSize),
      });
      const pullData = result.data[fieldName];
      const lastDocument = pullData.documents[pullData.documents.length - 1];
      const newCheckpoint = pullData.documents.length
        ? await buildCheckpoint(lastDocument)
        : (checkpoint ?? null);

      setIsPulling(false);
      setLastPulledAt(new Date().toISOString());
      setFailedToPull(false);
      backoffGate.onSuccess();
      onPullFromNetworkComplete?.();
      return {
        documents: pullData.documents.map(mapGqlToRx),
        checkpoint: newCheckpoint,
      };
    } catch (e) {
      console.error("Error in pull handler", {
        fieldName,
        name: (e as Error).name,
        message: (e as Error).message,
        e,
      });
      // These errors are later on wrapped in an RxError and sent to the errorSubscription
      // so we could just throw the original error here.
      Sentry.captureException(e);
      setIsPulling(false);
      setFailedToPull(true);
      backoffGate.onError();
      throw e;
    }
  };
}

const buildPushHandler =
  <RX, GQLInput, GQLOutput>({
    mutation,
    apolloClient,
    mapRxToGql,
    mapGqlToRx,
  }: {
    mutation: DocumentNode;
    apolloClient: ApolloClient<object>;
    mapRxToGql: (rxDoc: WithDeleted<RX>) => GQLInput;
    mapGqlToRx: (gqlDoc: GQLOutput) => WithDeleted<RX>;
  }) =>
  async (
    docs: Array<{
      newDocumentState: WithDeleted<RX>;
      assumedMasterState?: WithDeleted<RX>;
    }>,
  ): Promise<WithDeleted<RX>[]> => {
    const fieldName = getFieldResolverName(mutation);

    try {
      let _startTime: number;
      const variables = {
        input: {
          rows: docs.map((update) => ({
            newDocumentState: mapRxToGql(update.newDocumentState),
            assumedMasterState: update.assumedMasterState
              ? mapRxToGql(update.assumedMasterState)
              : undefined,
          })),
        },
      };
      const result = await apolloClient.mutate<{
        [key: string]: { rows: GQLOutput[] };
      }>({
        mutation: mutation,
        variables,
      });
      return result.data[fieldName].rows.map(mapGqlToRx);
    } catch (e) {
      const keysOfUpdates = docs.map((doc) =>
        typeof doc.newDocumentState === "object"
          ? getObjectKeys(doc.newDocumentState)
          : "",
      );
      console.error("Error in push handler", {
        fieldName,
        objectKeys: JSON.stringify(keysOfUpdates),
        name: (e as Error).name,
        // ⚠️ message or raw error object cannot be systematically logged
        // unless we find a way to filter out any PII/PHI from the error.
        // message: (e as Error).message,
        ...(e instanceof ApolloError || e instanceof TypeError
          ? { message: e.message.slice(0, 20) }
          : {}),
      });
      // ⚠️ These cannot be logged unless we find a way to filter out the
      // object from the message as they can contain PII/PHI.
      // Sentry.captureException(e);
      throw e;
    }
  };

const isUnderlyingNetworkError = (e: RxError) => {
  const underlyingErrors = e.parameters?.errors ?? [];
  return (
    underlyingErrors.length === 1 &&
    underlyingErrors[0].name === "ApolloError" &&
    underlyingErrors[0].message.includes("Failed to fetch")
  );
};
